/**
 * Which microphone, which speakers, which camera.
 *
 * Gordon, 22 October: "When a user presses call and chooses between a video
 * or a voice call – I want the user to be able to select devices on his
 * computer for webcam, audio and microphone pls – some users have multiple
 * systems. I want them to be able to choose."
 *
 * A desk with a docked laptop has a built-in microphone, a headset and a
 * speakerphone, and the browser's idea of "default" is whichever one Windows
 * last decided on - which is how a call ends up coming out of the laptop
 * under the lid while somebody talks into a headset nobody is listening to.
 * So the choice is made here, remembered per browser, and used by every call
 * from then on.
 *
 * THIS FILE TALKS TO navigator.mediaDevices AND TO localStorage, AND NOTHING
 * ELSE. No React, no page: the component that draws the three selects and the
 * page that opens the streams both come here, so the rules below are stated
 * once. Everything about the browser's behaviour that caught somebody out is
 * written next to the code that copes with it:
 *
 *   LABELS ARE EMPTY UNTIL A PERMISSION HAS BEEN GRANTED. enumerateDevices()
 *   lists every device at once, but their names are blank until the page has
 *   had one successful getUserMedia - a privacy rule, so a site cannot
 *   fingerprint a machine by its hardware without asking. The list has to be
 *   read AGAIN after the first stream opens, and the names shown in the
 *   meantime are "Microphone 1", not nothing.
 *
 *   A REMEMBERED DEVICE MAY NOT BE THERE. The headset is in a drawer; the
 *   dock is at the other desk. Its id is still in localStorage, and asking
 *   for it with `exact` fails with NotFoundError (or OverconstrainedError).
 *   So a remembered id is checked against the current list before it is
 *   used, and a missing one means the system default - with a note saying
 *   which device was expected, which is what tells somebody to plug it in.
 *   The memory itself is kept: plugging it back in brings it back.
 *
 *   CHOOSING SPEAKERS IS NOT EVERYWHERE. HTMLMediaElement.setSinkId exists in
 *   Chrome and Edge; Safari and Firefox do not have it, and there the output
 *   is whatever the system has chosen. The panel has to SAY so rather than
 *   offer a select that does nothing.
 */

const KEY = 'jarvis_call_devices';

/** The three kinds, in the order the panel shows them. */
export const KINDS = ['audioinput', 'audiooutput', 'videoinput'];

/** What each kind is called to a person. */
export const KIND_NAMES = {
  audioinput: 'Microphone',
  audiooutput: 'Speakers',
  videoinput: 'Camera'
};

const media = () => (typeof navigator !== 'undefined' ? navigator.mediaDevices : null) || null;

/** Whether this browser can list devices at all. */
export const supported = () => Boolean(media()?.enumerateDevices);

/**
 * Whether this browser lets a page choose where sound comes OUT.
 *
 * Chrome and Edge: yes. Safari and Firefox: no - the speakers are whatever
 * the system has chosen, and the panel says so in those words.
 */
export const canPickOutput = () => typeof HTMLMediaElement !== 'undefined'
  && typeof HTMLMediaElement.prototype.setSinkId === 'function';

/* ------------------------------------------------------------ the list */

/**
 * Every device, by kind, with whatever names the browser is willing to give.
 *
 * `labelled` is false while the names are blank - no permission yet - so the
 * panel can say "names appear once your microphone has been allowed" rather
 * than show three lists of numbers without explanation.
 */
export async function listDevices() {
  const out = { audioinput: [], audiooutput: [], videoinput: [], labelled: false };
  if (!supported()) return out;
  let rows = [];
  try { rows = await media().enumerateDevices(); } catch { return out; }
  for (const d of rows || []) {
    if (!out[d.kind]) continue;
    // Chrome lists the same physical device twice more under the ids
    // 'default' and 'communications'. The first is worth keeping - it IS the
    // system default, and choosing it is a real choice; the second is a
    // Windows-only alias that would show one headset three times.
    if (d.deviceId === 'communications') continue;
    out[d.kind].push({ deviceId: d.deviceId || '', label: d.label || '' });
    if (d.label) out.labelled = true;
  }
  return out;
}

/**
 * A device's name, as the select shows it.
 *
 * Chrome names its default entry "Default - Jabra Speak 510"; that becomes
 * "Jabra Speak 510 (system default)", which says the same thing in the order
 * somebody reads it. A blank label - no permission yet - becomes
 * "Microphone 2", which is at least a thing that can be chosen.
 */
export function nameOf(device, index, kind) {
  const base = String(device?.label || '').replace(/^(default|communications)\s*-\s*/i, '').trim();
  const name = base || `${KIND_NAMES[kind] || 'Device'} ${index + 1}`;
  return device?.deviceId === 'default' ? `${name} (system default)` : name;
}

/** Listen for a device being plugged in or pulled out. Returns the way to stop. */
export function onDeviceChange(fn) {
  const m = media();
  if (!m?.addEventListener) return () => {};
  m.addEventListener('devicechange', fn);
  return () => m.removeEventListener('devicechange', fn);
}

/* ---------------------------------------------------------- the memory */

const empty = () => ({ audioinput: null, audiooutput: null, videoinput: null });

/**
 * What was chosen, per kind: { id, label } or null for the system default.
 *
 * The label is kept beside the id so that a device that has gone can still
 * be NAMED in the note about it - the id alone is a hash the browser minted,
 * and "the device a1b2c3 is not plugged in" tells nobody anything.
 *
 * localStorage, in a try/catch: Safari in private mode throws on write, a
 * locked-down profile can throw on read, and a call must start either way.
 */
export function remembered() {
  const out = empty();
  try {
    const raw = JSON.parse(localStorage.getItem(KEY) || '{}');
    for (const kind of KINDS) {
      const v = raw?.[kind];
      if (v && typeof v.id === 'string' && v.id) out[kind] = { id: v.id, label: String(v.label || '') };
    }
  } catch { /* nothing remembered, or nowhere to remember it */ }
  return out;
}

/** Remember one kind's choice. Null, or the empty id, means the system default. */
export function remember(kind, device) {
  if (!KINDS.includes(kind)) return remembered();
  const next = remembered();
  next[kind] = device?.deviceId ? { id: device.deviceId, label: device.label || '' } : null;
  try {
    const stored = {};
    for (const k of KINDS) if (next[k]) stored[k] = next[k];
    localStorage.setItem(KEY, JSON.stringify(stored));
  } catch { /* a choice that cannot be kept still applies to this call */ }
  return next;
}

/**
 * The remembered choice, checked against what is actually plugged in.
 *
 *   { deviceId: 'abc', missing: null }     use it
 *   { deviceId: null,  missing: null }     nothing chosen: the system default
 *   { deviceId: null,  missing: {id,label} }  chosen, but not here: default,
 *                                           and the note names what was expected
 *
 * With no list yet (the first call, before anything has been enumerated) a
 * remembered id is trusted: the stream request itself will say if it is
 * wrong, and isGone() below is how the caller recognises that answer.
 */
export function resolve(kind, list, choice) {
  const want = choice?.[kind];
  if (!want?.id) return { deviceId: null, missing: null };
  const rows = list?.[kind];
  if (!Array.isArray(rows) || rows.length === 0) return { deviceId: want.id, missing: null };
  if (rows.some((d) => d.deviceId === want.id)) return { deviceId: want.id, missing: null };
  return { deviceId: null, missing: want };
}

/** The error a browser gives for a device that is not there. */
export const isGone = (err) => err?.name === 'NotFoundError'
  || err?.name === 'OverconstrainedError' || err?.name === 'DevicesNotFoundError';

/* ------------------------------------------------------- the constraints */

/**
 * The microphone request. `exact`, so that asking for the headset cannot be
 * quietly answered with the laptop - an `ideal` deviceId is a hint the
 * browser may ignore, and a wrong microphone is the fault nobody notices
 * until the other side says "you sound far away".
 */
export const audioConstraints = (deviceId) => ({
  echoCancellation: true,
  noiseSuppression: true,
  ...(deviceId ? { deviceId: { exact: deviceId } } : {})
});

/**
 * The camera request. With a chosen camera, that one; with none, the
 * front-facing one, which on a laptop is the only one and on a phone is the
 * one pointing at the person.
 */
export const videoConstraints = (deviceId) => (deviceId
  ? { width: { ideal: 1280 }, deviceId: { exact: deviceId } }
  : { width: { ideal: 1280 }, facingMode: 'user' });

/* ------------------------------------------------------------ the output */

/**
 * Send one element's sound to the chosen speakers.
 *
 * Resolves true when it was applied, false when this browser cannot (no
 * setSinkId) or refused - a sink id from another permission scope, say. The
 * empty string is the system default, which is also what undoing a choice
 * means. Never throws: an element that keeps playing through the default
 * speakers is a lesser failure than a call that stops.
 */
export async function applySink(el, deviceId) {
  if (!el || typeof el.setSinkId !== 'function') return false;
  try {
    await el.setSinkId(deviceId || '');
    return true;
  } catch {
    return false;
  }
}
